import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Bell, Send, RefreshCw, Loader2, User, Clock } from "lucide-react";
import { toast } from "sonner";

export default function IAInactivityPage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sprint-f/ia-usage/inactive-students", { params: { inactive_days: days } });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const notifyAll = async () => {
    if (!window.confirm(`Envoyer une notification aux parents/direction pour les ${data?.count || 0} élèves inactifs ?`)) return;
    setNotifying(true);
    try {
      const { data: r } = await api.post("/sprint-f/ia-usage/notify-inactive", null, { params: { inactive_days: days } });
      toast.success(`${r.notified} notifications envoyées`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setNotifying(false); }
  };

  return (
    <div className="space-y-6" data-testid="ia-inactivity-page">
      <div>
        <div className="chip mb-2"><AlertTriangle className="h-3 w-3" /><span>SUIVI IA PÉDAGOGIQUE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Élèves inactifs sur l&apos;espace IA</h1>
        <p className="text-slate-500 mt-1">Détectez les élèves qui n&apos;ont pas utilisé leur espace IA depuis N jours, et notifiez les parents.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-xs uppercase text-slate-500">Seuil (jours)</label>
          <Input type="number" value={days} onChange={e => setDays(parseInt(e.target.value, 10) || 7)} className="w-24" data-testid="inactive-days" />
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="gap-2" data-testid="btn-refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4" />Actualiser</>}
        </Button>
        <Button onClick={notifyAll} disabled={notifying || !data?.count} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="btn-notify-all">
          {notifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Notifier {data?.count || 0} parents</>}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">
          {data ? `${data.count} élèves inactifs depuis ${days}+ jours` : "Chargement..."}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Élève</th>
                <th className="px-4 py-3 text-left">Classe</th>
                <th className="px-4 py-3 text-left">Dernière activité</th>
              </tr>
            </thead>
            <tbody>
              {data?.students?.map(s => (
                <tr key={s.student_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`inactive-${s.student_id}`}>
                  <td className="px-4 py-2 font-medium flex items-center gap-2"><User className="h-3 w-3 text-slate-400" />{s.student_name}</td>
                  <td className="px-4 py-2 text-slate-600">{s.class_name}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {s.last_active_at ? new Date(s.last_active_at).toLocaleString("fr-FR") : "Jamais"}
                  </td>
                </tr>
              ))}
              {data?.students?.length === 0 && <tr><td colSpan={3} className="px-4 py-12 text-center text-slate-400">Aucun élève inactif — bravo ! 🎉</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
